import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook to manage the visibility of the certStatus column
 * ---
 * @param {*} param0.selectedColumns the columns that are currently selected for the table
 * @param {*} param0.handleColumnChange function to handle changes to the selected columns
 * @param {*} param0.setIncludeNotStartedCerts function to set the includeNotStartedCerts flag sent to backend
 * @returns
 */
const useCertStatusColumn = ({
  selectedColumns,
  handleColumnChange,
  setIncludeNotStartedCerts,
}) => {
  const [isCertStatusVisible, setIsCertStatusVisible] = useState(false);
  const hasAppliedDefaultCertStatusVisibility = useRef(false);

  // Will show or hide certStatus column on the next renders based on the dependencies
  useEffect(() => {
    // useTableManageColumns starts with [] and only loads the persisted
    // selection in a later effect. Acting before that either persists a
    // Status-only selection or flips includeNotStartedCerts (and every query
    // keyed on it) for one render.
    if (selectedColumns.length === 0) {
      return;
    }

    const isCertStatusSelected = selectedColumns.includes('certStatus');

    if (isCertStatusSelected) {
      hasAppliedDefaultCertStatusVisibility.current = true;
    }

    if (
      !hasAppliedDefaultCertStatusVisibility.current &&
      !isCertStatusSelected
    ) {
      handleColumnChange([...selectedColumns, 'certStatus']);
      return;
    }

    setIncludeNotStartedCerts(isCertStatusSelected);
    setIsCertStatusVisible(isCertStatusSelected);
  }, [
    handleColumnChange,
    setIncludeNotStartedCerts,
    selectedColumns, // selectedColumns must be part of the dependencies
  ]);

  const toggleCertStatusColumn = useCallback(() => {
    const index = selectedColumns.indexOf('certStatus');
    // certStatus is in selected columns, so we are removing it
    if (index > -1) {
      handleColumnChange(
        selectedColumns.filter((colName) => colName !== 'certStatus'),
      );
      setIncludeNotStartedCerts(false);
      // certStatus is NOT in selected columns, so we are adding it
    } else {
      handleColumnChange([...selectedColumns, 'certStatus']);
      setIncludeNotStartedCerts(true);
    }
    setIsCertStatusVisible((prev) => !prev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, handleColumnChange]);

  return {
    isCertStatusVisible,
    toggleCertStatusColumn,
    shouldNotStartedCertsButtonBeDisplayed: true,
  };
};

export default useCertStatusColumn;
